import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { postService, tagService, channelService, uploadService } from '../api/services';
import BlockEditor from '../components/BlockEditor';
import FileUpload from '../components/FileUpload';
import PollEditor from '../components/PollEditor';
import './CreatePostPage.css';

// 草稿暫存於瀏覽器 localStorage 的鍵值，發文成功或使用者捨棄後即清除
const DRAFT_KEY = 'tech-forum:draft';

const CreatePostPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const channelFromUrl = new URLSearchParams(location.search).get('channel') || 'ch-tech';

  const [formData, setFormData] = useState({
    title: '',
    tags: [],
    customTag: '',
    channel_id: channelFromUrl,
    post_type: 'DISCUSSION',
    next_post_id: '',
  });
  const [blocks, setBlocks] = useState([{ type: 'text', content: '' }]);
  const [attachments, setAttachments] = useState([]);
  // 投票（民調）：僅在所選頻道開啟「允許發投票文」時才會顯示與送出
  const [poll, setPoll] = useState({ enabled: false, question: '', poll_type: 'SINGLE', options: ['', ''] });
  // 重複問題偵測：依標題以全文檢索（FTS5）找出可能已回答的既有文章，零 AI 成本
  const [similarPosts, setSimilarPosts] = useState([]);
  const [similarDismissed, setSimilarDismissed] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // 偵測到的未發表草稿（供還原橫幅使用）；null 代表沒有可還原草稿
  const [draftPrompt, setDraftPrompt] = useState(null);
  // 下一篇推薦選擇器用：同頻道的文章列表
  const [channelPostsList, setChannelPostsList] = useState([]);

  // 進入頁面時讀取上次未發表草稿，僅在草稿確實有內容時才提示還原（不自動套用，交由使用者決定）
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      const hasContent = (draft.title && draft.title.trim())
        || (Array.isArray(draft.blocks) && draft.blocks.some((b) => b.content && b.content.trim()));
      if (hasContent) setDraftPrompt(draft);
    } catch (err) {
      // 草稿格式損壞時直接忽略，不影響發文流程
    }
  }, []);

  // 自動暫存：標題或內容有實質文字時，延遲 800ms 寫入草稿，避免每次按鍵都寫 localStorage。
  // 內容為空時不寫入，可避免一進頁面就把既有草稿覆蓋成空白。
  useEffect(() => {
    const hasContent = formData.title.trim() || blocks.some((b) => (b.content && b.content.trim()) || b.type === 'image');
    if (!hasContent) return undefined;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          title: formData.title,
          blocks,
          tags: formData.tags,
          customTag: formData.customTag,
          channel_id: formData.channel_id,
          post_type: formData.post_type,
          next_post_id: formData.next_post_id,
          savedAtLabel: new Date().toLocaleString('zh-TW', { hour12: false }),
        }));
      } catch (err) {
        // 配額不足等寫入失敗情況忽略，不阻斷發文
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData, blocks]);

  // 重複問題偵測：標題輸入後延遲 600ms 以 FTS 查相似既有文章（trigram 需至少 3 字元），結果供「可能重複」提示
  useEffect(() => {
    const title = formData.title.trim();
    if (title.length < 4) { setSimilarPosts([]); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const res = await postService.list({ search: title, limit: 5 });
        setSimilarPosts(res.data || []);
        setSimilarDismissed(false);
      } catch (err) {
        setSimilarPosts([]); // 查詢失敗不影響發文，靜默忽略
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.title]);

  // 當頻道改變時，查詢該頻道的文章列表（用於下一篇推薦選擇器）
  useEffect(() => {
    const fetchChannelPosts = async () => {
      try {
        const res = await postService.list({ channel: formData.channel_id, limit: 50 });
        setChannelPostsList(res.data || []);
      } catch (err) {
        setChannelPostsList([]); // 查詢失敗不影響發文，靜默忽略
      }
    };
    if (formData.channel_id) {
      fetchChannelPosts();
    }
  }, [formData.channel_id]);

  // 還原草稿：將草稿內容套回表單與內容區塊，並關閉提示橫幅
  const restoreDraft = () => {
    if (!draftPrompt) return;
    setFormData((prev) => ({
      ...prev,
      title: draftPrompt.title || '',
      tags: Array.isArray(draftPrompt.tags) ? draftPrompt.tags : [],
      customTag: draftPrompt.customTag || '',
      channel_id: draftPrompt.channel_id || prev.channel_id,
      post_type: draftPrompt.post_type || prev.post_type,
      next_post_id: draftPrompt.next_post_id || '',
    }));
    if (Array.isArray(draftPrompt.blocks) && draftPrompt.blocks.length) setBlocks(draftPrompt.blocks);
    setDraftPrompt(null);
  };

  // 捨棄草稿：清除 localStorage 並關閉提示橫幅
  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (err) { /* 忽略清除失敗 */ }
    setDraftPrompt(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tagRes, chRes] = await Promise.all([
          tagService.list(),
          channelService.list(),
        ]);
        setAllTags(tagRes.data);
        setChannels(chRes.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTagToggle = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((id) => id !== tagId)
        : [...prev.tags, tagId],
    }));
  };

  const getRequiredFields = () => {
    const selectedChannel = channels.find((ch) => ch.id === formData.channel_id);
    try {
      return JSON.parse(selectedChannel?.required_fields || '[]');
    } catch (err) {
      return [];
    }
  };

  // 解析所選頻道的選用功能（features），判斷是否允許在本頻道發投票文
  const channelAllowsPoll = () => {
    const selectedChannel = channels.find((ch) => ch.id === formData.channel_id);
    try {
      return JSON.parse(selectedChannel?.features || '[]').includes('poll');
    } catch (err) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitAttempted(true);

    const hasContent = blocks.some(b => (b.content && b.content.trim()) || b.type === 'image');
    if (!formData.title.trim() || !hasContent) {
      setError('標題和內容不能為空');
      return;
    }

    const requiredFields = getRequiredFields();
    // 業務邏輯：不同頻道可由 Admin 決定發文必填項目，前端送出前先做提示與驗證，後端仍會再次檢查。
    if (requiredFields.includes('tags') && formData.tags.length === 0 && !formData.customTag.trim()) {
      setError('此頻道至少需要一個標籤');
      return;
    }

    // 附投票驗證：頻道允許且使用者勾選「加入投票」時，題目與至少兩個有效選項為必填
    const payload = { ...formData, content: JSON.stringify(blocks) };
    if (channelAllowsPoll() && poll.enabled) {
      const opts = poll.options.map((o) => o.trim()).filter(Boolean);
      if (!poll.question.trim() || opts.length < 2) {
        setError('投票需填寫題目與至少兩個選項');
        return;
      }
      payload.poll = { question: poll.question.trim(), poll_type: poll.poll_type, options: opts };
    }

    setSubmitting(true);

    try {
      const res = await postService.create(payload);

      // Link attachments to post
      if (attachments.length > 0) {
        await Promise.all(
          attachments.map(a => uploadService.linkToPost(a.id, res.data.id))
        );
      }

      // 發文成功後清除暫存草稿，避免下次進入頁面又提示還原已發表內容
      try { localStorage.removeItem(DRAFT_KEY); } catch (err) { /* 忽略清除失敗 */ }
      navigate(`/posts/${res.data.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="container mt-5">載入中...</div>;
  const requiredFields = getRequiredFields();
  const tagsRequired = requiredFields.includes('tags');
  const pollAllowed = channelAllowsPoll();

  return (
    <div className="create-post-page">
      <div className="container">
        <h1>發表新文章</h1>

        {draftPrompt && (
          <div className="draft-banner">
            <span className="draft-banner-text">
              💾 偵測到未發表的草稿{draftPrompt.savedAtLabel ? `（${draftPrompt.savedAtLabel}）` : ''}，要還原嗎？
            </span>
            <div className="draft-banner-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={restoreDraft}>還原草稿</button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={discardDraft}>捨棄</button>
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit} className="post-form">
          <div className="form-group">
            <label htmlFor="channel_id"><span className="required-label">* 頻道</span></label>
            <select
              id="channel_id"
              name="channel_id"
              className={`form-control required-placeholder ${submitAttempted && !formData.title.trim() ? 'required-missing' : ''}`}
              value={formData.channel_id}
              onChange={handleChange}
              disabled={submitting}
              required
            >
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  {ch.icon} {ch.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="post_type"><span className="required-label">* 文章類型</span></label>
            <select
              id="post_type"
              name="post_type"
              className="form-control"
              value={formData.post_type}
              onChange={handleChange}
              disabled={submitting}
              required
            >
              <option value="DISCUSSION">討論中</option>
              <option value="EXPERIENCE">經驗分享</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="title"><span className="required-label">* 標題</span></label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              placeholder="請輸入文章標題"
              required
              disabled={submitting}
            />
            {!similarDismissed && similarPosts.length > 0 && (
              <div className="similar-posts-hint">
                <div className="similar-posts-head">
                  <span>💡 以下既有文章可能已回答你的問題，發文前可先確認：</span>
                  <button type="button" className="similar-posts-dismiss" onClick={() => setSimilarDismissed(true)}>略過</button>
                </div>
                <ul className="similar-posts-list">
                  {similarPosts.map((p) => (
                    <li key={p.id}>
                      <a href={`/posts/${p.id}`} target="_blank" rel="noopener noreferrer">{p.title}</a>
                      <span className="similar-posts-meta">{p.comment_count || 0} 則回應{p.status === 'RESOLVED' ? '．已解決' : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="form-group">
            <label><span className="required-label">* 內容</span> （可新增多個文字與程式碼區塊）</label>
            <BlockEditor blocks={blocks} onChange={setBlocks} />
          </div>

          <div className="form-group">
            <label>附件上傳</label>
            <FileUpload attachments={attachments} onAttachmentsChange={setAttachments} />
          </div>

          <div className="form-group">
            <label>{tagsRequired ? <span className="required-label">* 標籤</span> : '標籤'} <span className="optional-hint">(可多選{tagsRequired ? '，此頻道必填；與下方自訂標籤擇一即可' : '，此頻道選填'})</span></label>
            <div className={`tags-selector ${submitAttempted && tagsRequired && formData.tags.length === 0 && !formData.customTag.trim() ? 'required-missing' : ''}`}>
              {allTags.map((tag) => (
                <label key={tag.id} className="tag-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.tags.includes(tag.id)}
                    onChange={() => handleTagToggle(tag.id)}
                    disabled={submitting}
                  />
                  {tag.name}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="customTag">自訂標籤{tagsRequired ? '（系統標籤不適用時改填此欄，與系統標籤擇一即可）' : '（可選）'}</label>
            <input
              type="text"
              id="customTag"
              name="customTag"
              className="form-control"
              value={formData.customTag}
              onChange={handleChange}
              placeholder="輸入自訂標籤（只能輸入一個）"
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="next_post_id">推薦接著看<span className="optional-hint">（選填，可引導讀者閱讀相關文章）</span></label>
            <select
              id="next_post_id"
              name="next_post_id"
              className="form-control"
              value={formData.next_post_id}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="">-- 無推薦 --</option>
              {channelPostsList.map((post) => (
                <option key={post.id} value={post.id}>
                  {post.title}
                </option>
              ))}
            </select>
          </div>

          {pollAllowed && (
            <div className="form-group">
              <label>投票 <span className="optional-hint">(此頻道可附投票，選填)</span></label>
              <PollEditor value={poll} onChange={setPoll} />
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? '發表中...' : '發表'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/')}
              disabled={submitting}
            >
              取消
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePostPage;

